import { createAsyncThunk, Dispatch, ThunkAction, ThunkDispatch } from '@reduxjs/toolkit';
import {
  AppState,
  setFontSize,
  setGatewayLogs,
  setLanguage,
  setLoadingProjects,
  setLoadingSites,
  setNotifications,
  addNotifications,
  setProjects,
  setSelectedTool,
  setTheme,
  setUpdateServiceStatus,
  markNotificationsRead,
  prependGatewayLogs,
} from './app-slice';
import {
  AddCollectionEvent,
  DataCollection,
  GatewaySettingsType,
  MoveDataToStorage,
  Event,
  EventType,
  InitiateComputeSearchParams,
  InitiateDMSearchParams,
} from './utils/events';
import { APIPrefix, buildUrl, hasSessionExpired } from './utils/functions';
import { getBrowserPreferredDarkMode } from './utils/common/constants';
import {
  CanfarServiceType,
  ComputeServiceType,
  createAppAsyncThunk,
  DataItemType,
  GatewayLogType,
  JobType,
  NavigateEventContext,
  NotificationType,
  Project,
  Site,
  Theme,
  ToolType,
  toolTypeToPath,
} from './utils/types';
import {
  rS_setDefaultProjectSetting,
  rS_setDefaultSiteUUIDSetting,
  rS_setFontSizeSetting,
  rS_setIconSizeSetting,
  rS_setLanguageSetting,
  rS_setPopupLogsEnabledSetting,
  rS_setPopupNotificationsEnabledSetting,
  rS_setThemeSetting,
} from './app/settings-slice';
import {
  GlobalState,
  rG_setComputeServicesLoaded,
  rG_setDarkMode,
  rG_setDataManagementTokenObtained,
  rG_setGatewayBackendToken,
  rG_setLoggedIn,
  rG_setLoggedInRefreshTrigger,
  rG_setPreferredUsername,
  rG_setProject,
  rG_setSelectedCanfar,
  rG_setSessionValid,
  rG_setSite,
  rG_setSiteCapabilitiesTokenObtained,
  rG_setSubmittingJob,
  rG_setUsername,
} from './app/global-slice';
import { getProjectFiles } from './components/project/project-actions';
import {
  DataState,
  rD_setCanfarServices,
  rD_setComputeServices,
  rD_setSites,
} from './app/data-slice';
import { fnD_getComputeJobs, loadStorageAreas } from './app/data-actions';
import { AppDispatch, RootState } from './app/store';
import i18n from './i18n/i18n';
import { loadComputeData } from './components/search-compute/search-compute-actions';
import { initiateDMSearch } from './components/data-management/file-browser-actions';
import {
  setDataManagementJobs,
  setJobsLoading,
} from './components/data-management/file-browser-slice';

import { EventTarget } from './utils/events';
import { eventHandler as handleHomeEvent } from './components/home/home-actions';
import { eventHandler as handleSearchCatalogEvent } from './components/search-catalog/search-catalog-actions';
import { eventHandler as handleFileBrowserEvent } from './components/data-management/file-browser-actions';
import { useDispatch } from 'react-redux';
import { showSuccessToast } from './components/gateway-toast/success-message-toast';
import { showErrorToast } from './components/gateway-toast/error-message-toast';

// Type for a thunk action returning a Promise<void>
type AppThunk = ThunkAction<Promise<void>, RootState, unknown, any>;

export const persistLanguageSelection = createAsyncThunk(
  'app/persistLanguageSelection',
  async (language: string, thunkAPI: any) => {
    console.log(`Thunk Language Setter: ${language}`);

    localStorage.setItem('gateway_language', language);
    i18n.changeLanguage(language);
    thunkAPI.dispatch(setLanguage(language));
  },
); // persistLanguageSelection

export const persistAppState = createAsyncThunk(
  'app/persistAppState',
  async (appState: Partial<GlobalState>, thunkAPI: any) => {
    sessionStorage.setItem('appState', JSON.stringify(appState));
    // dispatch reducers
    thunkAPI.dispatch(rG_setLoggedIn(appState.loggedIn));
    thunkAPI.dispatch(rG_setSessionValid(appState.sessionValid));
    thunkAPI.dispatch(rG_setLoggedInRefreshTrigger(appState.loggedInRefreshTrigger));
    thunkAPI.dispatch(
      rG_setSiteCapabilitiesTokenObtained(appState.siteCapabilitiesTokenObtained),
    );
    thunkAPI.dispatch(
      rG_setDataManagementTokenObtained(appState.dataManagementTokenObtained),
    );
    thunkAPI.dispatch(rG_setUsername(appState.username));
    thunkAPI.dispatch(rG_setPreferredUsername(appState.preferredUsername));
    thunkAPI.dispatch(rG_setDarkMode(appState.darkMode ?? false));
  },
);

//	-------------------------------------------------
//
//	Set user preferences in the database.
//
//	-------------------------------------------------
export const setPreferences = createAsyncThunk(
  'app/setPreferences',
  async (args: GatewaySettingsType, thunkAPI: any) => {
    // update the settings in the context.
    if (args.theme !== undefined) {
      //
      const darkMode: boolean =
        args.theme === Theme.AUTO
          ? getBrowserPreferredDarkMode()
          : args.theme === Theme.DARK;

      // set theme held in cookie.
      thunkAPI.dispatch(setTheme(darkMode ? 'dark' : 'light'));

      // set theme held in Redux state.
      thunkAPI.dispatch(rS_setThemeSetting(args.theme));

      // set theme held in app state.
      thunkAPI.dispatch(rG_setDarkMode(darkMode));
      //
    }
    if (args.language !== undefined) {
      thunkAPI.dispatch(rS_setLanguageSetting(args.language));
    }
    if (args.fontSize !== undefined) {
      thunkAPI.dispatch(rS_setFontSizeSetting(args.fontSize));
      thunkAPI.dispatch(setFontSize(args.fontSize));
    }
    if (args.defaultProject !== undefined) {
      thunkAPI.dispatch(rS_setDefaultProjectSetting(args.defaultProject));
    }
    if (args.defaultSiteUUID !== undefined) {
      thunkAPI.dispatch(rS_setDefaultSiteUUIDSetting(args.defaultSiteUUID));
    }
    if (args.iconSize !== undefined) {
      thunkAPI.dispatch(rS_setIconSizeSetting(args.iconSize));
    }
    if (args.popupNotificationsEnabled !== undefined) {
      thunkAPI.dispatch(
        rS_setPopupNotificationsEnabledSetting(args.popupNotificationsEnabled),
      );
    }
    if (args.popupLogsEnabled !== undefined) {
      thunkAPI.dispatch(rS_setPopupLogsEnabledSetting(args.popupLogsEnabled));
    }

    try {
      var url = APIPrefix() + '/v1/set_preferences?';

      if (args.theme !== undefined) {
        url += 'theme=' + args.theme + '&';
      }
      if (args.language !== undefined) {
        url = url + 'language=' + args.language + '&';
      }
      if (args.iconSize !== undefined) {
        url = url + 'icon_size=' + args.iconSize + '&';
      }
      if (args.fontSize !== undefined) {
        url = url + 'font_size=' + args.fontSize + '&';
      }
      if (args.defaultProject !== undefined) {
        url = url + 'default_project=' + args.defaultProject + '&';
      }
      if (args.defaultSiteUUID !== undefined) {
        url = url + 'default_site_uuid=' + args.defaultSiteUUID + '&';
      }
      if (args.popupNotificationsEnabled !== undefined) {
        url = url + 'popup_notifications_enabled=' + args.popupNotificationsEnabled + '&';
      }
      if (args.popupLogsEnabled !== undefined) {
        url = url + 'popup_errors_enabled=' + args.popupLogsEnabled + '&';
      }

      url = url.slice(0, -1);

      console.log('Preference>>>>>>' + url);

      // call the /set_preferences end point of the backend API.
      const apiResult = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      // HTTP 200 means the update succeeded.
      if (apiResult.status === 200) {
        //
        //
      }

      // HTTP 401 means the session has expired.
      if (apiResult.status === 401) {
        console.log('setPreferences 401.');
      }
    } catch (e) {}
  },
);

//	------------------------------------------------------------
//
//	Check if the user session has expired every time the counter
//	polls.
//
//	------------------------------------------------------------

export const checkUserSession = createAsyncThunk(
  'app/checkUserSession',
  async (_, thunkAPI: any) => {
    try {
      const session_valid = await hasSessionExpired();
      // update the state that holds if the current login session is valid.
      thunkAPI.dispatch(rG_setSessionValid(session_valid));
    } catch (e) {}
  },
); // checkUserSession

//	------------------------------------------------------------
//
//	Get a list of CANFAR services from local compute state
//	array.
//
//	------------------------------------------------------------

async function _getCanfarServices(
  dispatch: Dispatch,
  getState: () => RootState,
  signal?: AbortSignal,
) {
  const stateFetch: any = getState();
  const stateData: DataState = stateFetch.data;
  const stateGlobal: GlobalState = stateFetch.global;
  // loop through the local compute services to identify items of type 'CANFAR'.
  var canfarServices: CanfarServiceType[] = [];
  const computeServices: ComputeServiceType[] = stateData.computeServices ?? [];

  for (let index = 0; index < computeServices.length; index++) {
    if (computeServices[index].session_type.toUpperCase() === 'CANFAR') {
      // add new canfar service.
      const newService: CanfarServiceType = {
        id: computeServices[index].compute_service_id,
        url: computeServices[index].host_url ?? '',
        site: computeServices[index].site,
        name: computeServices[index].service_name,
      };
      canfarServices.push(newService);
    }
  }
  // update the state with the list of returned site services.
  dispatch(rD_setCanfarServices(canfarServices));

  // select the first service, if none are currently selected.
  if (
    stateGlobal.selectedCanfar === undefined &&
    canfarServices.length > 0 &&
    stateGlobal.site !== undefined
  ) {
    for (let i: number = 0; i < canfarServices.length; i++) {
      if (canfarServices[i].site.site_uuid === stateGlobal.site.site_uuid) {
        dispatch(rG_setSelectedCanfar(canfarServices[i]));
        return;
      }
    }
  }
} // getCanfarServices

//	------------------------------------------------------------
//
//	An asynchronous function that loads a list of local
//	compute services from the site-capabilities API.
//
//	------------------------------------------------------------

async function _loadComputeServices(
  dispatch: Dispatch,
  getState: () => RootState,
  signal?: AbortSignal,
) {
  var urlCommand: string =
    APIPrefix() +
    '/v1/site_capabilities/list_compute?service_type=jupyterhub,canfar,carta';
  const stateFetch: any = getState();
  const stateData: DataState = stateFetch.data;

  try {
    const apiResult = await fetch(urlCommand, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (apiResult.status === 200) {
      var compute: object[] = [];
      var computeServices: ComputeServiceType[] = [];

      const computeJson = await apiResult.json();
      if (computeJson !== undefined) {
        compute = computeJson;
      }

      // extract the information we need.
      for (var i: number = 0; i < compute.length; i++) {
        var site: any = compute[i];

        // get the site from the sites list.
        let siteIndex: number = stateData.sites.findIndex(
          (item) => item.site_uuid === site.site_uuid.toLowerCase(),
        );

        if ('associated_services' in site && 'site_uuid' in site)
          if (site['associated_services'].length > 0 && siteIndex > -1) {
            for (var j = 0; j < site.associated_services.length; j++) {
              var newComputeService: ComputeServiceType = {
                site:
                  siteIndex > -1
                    ? stateData.sites[siteIndex]
                    : {
                        node: site.node.toUpperCase(),
                        site: site.site.toUpperCase(),
                        site_uuid: site.site_uuid.toLowerCase(),
                      },
                compute_service_id: site.associated_services[j].id,
                resource_type: 'srcnet',
                session_type: site.associated_services[j].type ?? '',
                service_url: buildUrl({
                  prefix: site.associated_services[j].prefix,
                  host: site.associated_services[j].host,
                  path: site.associated_services[j].path,
                  port: site.associated_services[j].port,
                }),
                host_url:
                  (site.associated_services[j].type ?? '').toUpperCase() === 'CANFAR'
                    ? buildUrl({
                        prefix: site.associated_services[j].prefix,
                        host: site.associated_services[j].host,
                        path: '',
                        port: site.associated_services[j].port,
                      })
                    : undefined,
                is_force_disabled: site.associated_services[j].is_force_disabled ?? false,
                downtime: site.associated_services[j].downtime ?? [],
                service_name: site.associated_services[j].name ?? '',
                active_status: site.associated_services[j].active_status ?? false,
                health_status: site.associated_services[j].health_status ?? 'unknown',
                top_banner: site.associated_services[j].top_banner ?? 'unknown status',
                bottom_banner_type:
                  site.associated_services[j].bottom_banner_type ?? 'none',
                bottom_banner: site.associated_services[j].bottom_banner ?? '',
                active_session_count: site.associated_services[j].active_session_count,
                is_embedded:
                  site.associated_services[j].name?.toUpperCase().indexOf('(EMBED)') >
                    -1 ?? false,
                compute_status: site.associated_services[j].active_status
                  ? 'available'
                  : 'unavailable',
              };
              computeServices.push(newComputeService);
            }
          }
      }

      // update the state with the list of returned computer services.
      dispatch(rD_setComputeServices(computeServices));
      dispatch(rG_setComputeServicesLoaded(true));
    }

    // HTTP 401 means the session has expired.
    if (apiResult.status === 401) {
      console.log('Failed to load local compute services.');
    }
  } catch (e) {
    console.log(e);
  }
} // loadComputeServices

//	-------------------------------------------------
//
//	load the list of projects that this user is
//	a member of.
//
//	-------------------------------------------------

async function _loadProjects(
  dispatch: Dispatch,
  getState: () => RootState,
  signal?: AbortSignal,
) {
  try {
    dispatch(setLoadingProjects(true));
    const apiResult = await fetch(APIPrefix() + '/v1/get_projects', {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (apiResult.status === 200) {
      const returnedJson = await apiResult.json();

      if (returnedJson.projects !== undefined) {
        var projects: Project[] = [];
        for (var i: number = 0; i < returnedJson.projects.length; i++) {
          var newProject: Project = {
            id: returnedJson.projects[i].project_id,
            name: returnedJson.projects[i].name,
            description: returnedJson.projects[i].description,
            abbreviation: returnedJson.projects[i].abbreviation,
            icon: returnedJson.projects[i].icon,
            iconBW: returnedJson.projects[i].icon,
            //iconBW: imageToGreyscale({
            //  icon: returnedJson.projects[i].icon,
            //  width: 32,
            //  height: 32,
            //}),
          };
          projects.push(newProject);
        }

        // set state.
        dispatch(setProjects(projects));

        // see if the session has a currently-selected project.
        const projectID: {} = JSON.parse(sessionStorage.getItem('project_id') ?? '{}');

        if ('id' in projectID) {
          const project:
            | {
                id: number;
                name: string;
                description: string;
                abbreviation: string;
                icon: string;
                iconBW: string;
              }
            | undefined = projects.find((item) => item.id === projectID.id);
          if (project !== undefined) {
            dispatch(rG_setProject({ id: project.id, name: project.name }));
          }
        }
        //
      }
    }
  } catch (e) {}
  dispatch(setLoadingProjects(false));
} // loadProjects

//	------------------------------------------------------------
//
//	An asynchronous function that loads a list of sites from
//	the site-capabilities API.
//
//	------------------------------------------------------------

async function _loadSites(
  dispatch: Dispatch,
  getState: () => RootState,
  signal?: AbortSignal,
) {
  //
  var urlCommand: string = APIPrefix() + '/v1/site_capabilities/list_sites';
  //const stateFetch: any = getState();
  //const state: GlobalState = stateFetch.global;

  try {
    dispatch(setLoadingSites(true));
    const apiResult = await fetch(urlCommand, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    // Return code 200 means the API has run.
    if (apiResult.status === 200) {
      //
      const nodesAndSites = await apiResult.json();
      let sites: Site[] = [];
      for (let node: number = 0; node < nodesAndSites.length; node++) {
        for (let site: number = 0; site < nodesAndSites[node].sites.length; site++) {
          const newSite: Site = {
            node: nodesAndSites[node].node.toUpperCase(),
            site: nodesAndSites[node].sites[site].site.toUpperCase(),
            site_uuid: nodesAndSites[node].sites[site].site_uuid.toLowerCase(),
            icon: nodesAndSites[node].sites[site].icon,
            iconBW: nodesAndSites[node].sites[site].icon,
            //iconBW: imageToGreyscale({
            //  icon: nodesAndSites[node].sites[site].icon,
            //  width: 64,
            //  height: 64,
            //}),
            index: nodesAndSites[node].sites[site].id,
            latitude: nodesAndSites[node].sites[site].latitude,
            longitude: nodesAndSites[node].sites[site].longitude,
          };
          sites.push(newSite);
        }
      }

      // update the state.
      dispatch(rD_setSites(sites));

      // see if the session has a currently-selected site.
      const siteUUID: string = sessionStorage.getItem('site_uuid') ?? '';

      const site: Site | undefined = sites.find((item) => item.site_uuid === siteUUID);

      if (site !== undefined) {
        dispatch(rG_setSite(site));
      }
      //
    } else {
      dispatch(rD_setSites([]));
    }
    dispatch(setLoadingSites(false));
  } catch (e) {
    dispatch(setLoadingSites(false));
  }
  //
} // loadSites

//	-------------------------------------------------
//
//	retrieve backend token
//
//	-------------------------------------------------

async function _retrieveBackendToken(
  dispatch: Dispatch,
  getState: () => RootState,
  signal?: AbortSignal,
) {
  console.log('retrieveBackendToken() fires');
  try {
    // call the /get_tokens endpoint here.
    const apiResult = await fetch(APIPrefix() + '/v1/get_tokens', {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (apiResult.status === 200) {
      const returnedJson = await apiResult.json();
      // set backend_gateway_token
      dispatch(rG_setGatewayBackendToken(returnedJson.gateway_backend_token));
    }
  } catch (e) {}
} // retrieveBackendToken

//	-------------------------------------------------
//
//	refresh notifications
//
//	-------------------------------------------------

export const getNotifications = createAsyncThunk(
  'app/getNotifications',
  async (_, thunkAPI: any) => {
    try {
      // call the /get_notifications endpoint here.
      const apiResult = await fetch(APIPrefix() + '/v1/get_notifications', {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (apiResult.status === 200) {
        const returnedJson = await apiResult.json();

        var notifications: NotificationType[] = [];

        // loop through the returned notifications, and add them to the array.
        for (var i: number = 0; i < returnedJson.notifications.length; i++) {
          var newNotification: NotificationType = {
            notificationID: returnedJson.notifications[i].notification_id,
            notificationType: returnedJson.notifications[i].notification_type,
            notificationText: returnedJson.notifications[i].notification_text,
            readFlag: returnedJson.notifications[i].read_flag,
            additionalInfo: returnedJson.notifications[i].additional_info,
            createdAt: returnedJson.notifications[i].created_at,
            displayPopup: false,
          };
          notifications.push(newNotification);
        }
        // update the state.
        thunkAPI.dispatch(addNotifications(notifications));
      }
    } catch (e) {}
  },
); // getNotifications

//	------------------------------------------------------------
//
//	An asynchronous function that loads a list of data-management
//	API jobs.
//
//	------------------------------------------------------------
export const fetchJobs = createAsyncThunk('app/fetchJobs', async (_, thunkAPI: any) => {
  thunkAPI.dispatch(setJobsLoading(true));
  try {
    const urlCommand = APIPrefix() + '/v1/data_management/list_jobs';

    const apiResult = await fetch(urlCommand, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (apiResult.status === 200) {
      const returnedJson = await apiResult.json();

      let jobsList: JobType[] = returnedJson.jobs_list ?? [];

      // remove "NOT FOUND"
      jobsList = jobsList.filter((j) => j.status !== 'NOT FOUND');

      thunkAPI.dispatch(setDataManagementJobs(jobsList));
    } else if (apiResult.status === 401) {
      console.log('Session expired (401).');
      thunkAPI.dispatch(setDataManagementJobs([]));
    } else {
      thunkAPI.dispatch(setDataManagementJobs([]));
    }
  } catch (err) {
    console.error(err);
    thunkAPI.dispatch(setDataManagementJobs([]));
  } finally {
    thunkAPI.dispatch(setJobsLoading(false));
  }
});
//	-------------------------------------------------
//
//	raiseEvent related actions
//
//	-------------------------------------------------

//	-------------------------------------------------
//
//	delete a list of notifications from the DB.
//
//	-------------------------------------------------

async function _deleteNotifications(
  notificationIDs: number[],
  dispatch: AppDispatch,
  getState: () => RootState,
  signal?: AbortSignal,
) {
  console.log('deleteNotifications fires:');
  console.log(notificationIDs);

  const stateFetch: any = getState();
  const state: AppState = stateFetch.app;

  // remove the notification from the list.
  var notifications: NotificationType[] = state.notifications.slice();
  for (var i: number = 0; i < notificationIDs.length; i++) {
    const notificationPos: number = notifications.findIndex(
      (item) => item.notificationID === notificationIDs[i],
    );
    if (notificationPos > -1) notifications.splice(notificationPos, 1);
  }
  dispatch(setNotifications(notifications));

  // raise a global refresh notifications event.
  // immediately after deleting the notification,
  // it refreshes the component to get the latest/refreshed notifications
  const newEvent: Event = {
    eventTarget: EventTarget.GLOBAL,
    eventType: EventType.REFRESH_NOTIFICATIONS,
  };
  dispatch(handleEvent({ event: newEvent }));

  try {
    // call the /delete_notification end point here.
    const apiResult = await fetch(APIPrefix() + '/v1/delete_notification', {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(notificationIDs),
    });

    console.log(apiResult);

    // HTTP 401 means the session has expired.
    if (apiResult.status === 401) {
      console.log('deleteNotifications 401.');
    }
  } catch (e) {
    console.log('error:');
    console.log(e);
  }
} // deleteNotifications

//	-------------------------------------------------
//
//	mark a list of notifications as READ in the DB.
//
//	-------------------------------------------------

async function _markNotificationsRead(
  notificationIDs: number[],
  dispatch: AppDispatch,
  getState: () => RootState,
  signal?: AbortSignal,
) {
  dispatch(markNotificationsRead(notificationIDs));
  try {
    // call the /mark_notifications_read end point here.
    const apiResult = await fetch(APIPrefix() + '/v1/mark_notifications_read', {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(notificationIDs),
    });
  } catch (e) {
    console.log(e);
  }
} // markNotificationsRead

//	------------------------------------------------------------
//
//	Call the API to move data to a storage area.
//
//	------------------------------------------------------------

async function _moveDataToStorage(
  settings: MoveDataToStorage,
  dispatch: AppDispatch,
  signal?: AbortSignal,
) {
  //
  console.log('moveDataToStorage() fires:', settings);
  var urlCommand: string = APIPrefix() + '/v1/data_management/move_data?';

  // token.
  urlCommand =
    urlCommand +
    'to_storage_area_uuid=' +
    settings.toStorageAreaUUID +
    '&' +
    'to_site_uuid=' +
    settings.toSiteUUID +
    '&' +
    'to_storage_identifier=' +
    settings.toStorageIdentifier +
    '&' +
    'lifetime=' +
    settings.lifetime.toString();

  // map over the files to move, because we are using namespace and filename, and the
  // data-management API wants namespace and name.
  const requestBody: {
    namespace: string;
    name: string;
  }[] = settings.filesToMove.map((item: DataItemType) => {
    return {
      namespace: item.namespace,
      name: item.filename,
    };
  });

  var jobSubmittedSuccessfully: boolean = false;
  try {
    const apiResult = await fetch(urlCommand, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    });
    if (apiResult.status === 200) {
      //
      const returnedJson = await apiResult.json();

      // get job ID.
      var jobID: string = '';
      if (returnedJson.job_id !== undefined) jobID = returnedJson.job_id;

      // update the state with the job ID.
      console.log('Submitted job id:');
      console.log(jobID);

      // refresh the data-management jobs panel.
      dispatch(fetchJobs());
      jobSubmittedSuccessfully = true;
      //
    }

    // if the return code is 401 then session has expired.
    if (apiResult.status === 401) {
      console.log('moveDataToStorage 401.');
    }

    // if the return code is 403 then we don't have a token. either we're
    // not logged in, or we're not authorised to the data-management API.
  } catch (e) {
    console.log(e);
  }

  // turn off the job-submitting flag.
  dispatch(rG_setSubmittingJob(false));
  //
} // moveDataToStorage

//	------------------------------------------------------------
//
//	Create a new collection in the database.
//
//	------------------------------------------------------------
async function _createNewDataCollection(
  event: Event,
  dispatch: AppDispatch,
  getState: () => RootState,
  signal?: AbortSignal,
) {
  const stateFetch = getState();
  const darkMode = stateFetch.global.darkMode;
  const { eventTarget, eventType, parameters } = event;
  const dataCollection = parameters as DataCollection;

  console.log('createNewDataCollection fires:');
  console.log(dataCollection);
  if (dataCollection.dataCollection !== undefined)
    if (
      dataCollection.dataCollection.name !== undefined &&
      dataCollection.dataCollection.name !== ''
    ) {
      var urlCommand: string =
        APIPrefix() +
        '/v1/add_data_collection' +
        '?project_id=' +
        (dataCollection.project !== undefined
          ? dataCollection.project.id.toString()
          : '-1') +
        '&name=' +
        dataCollection.dataCollection.name;

      try {
        const apiResult = await fetch(urlCommand, {
          method: 'PUT',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body:
            dataCollection.dids !== undefined
              ? JSON.stringify(dataCollection.dids)
              : JSON.stringify([]),
        });

        const returnedJson = await apiResult.json();

        // HTTP 201 means the collection has been added.
        if (apiResult.status === 201) {
          // get the number of items added.
          var itemsAdded: number = 0;
          try {
            itemsAdded = returnedJson.additional_info.items_added;
          } catch (e) {}

          // Display confirmation message
          const params: AddCollectionEvent = {
            collectionName:
              dataCollection.dataCollection !== undefined
                ? dataCollection.dataCollection.name
                : '',
            projectName:
              dataCollection.project !== undefined ? dataCollection.project.name : '',
            itemsAdded: itemsAdded,
            messageTarget:
              dataCollection.messageSearchCatalog === true
                ? EventTarget.SEARCH_CATALOG_SELECTED_ITEMS_TAB
                : undefined,
          };
          const newEvent: Event = {
            eventTarget: eventTarget,
            eventType: EventType.DATA_COLLECTION_ADDED,
            parameters: params,
          };
          dispatch(
            handleEvent({
              event: newEvent,
              ctx: {
                showSuccessToast: (message: string) =>
                  showSuccessToast(message, darkMode),
              },
            }),
          );
        }

        // HTTP 400 means a data collection with this name already exists.
        else if (apiResult.status === 400) {
          // Display confirmation message
          const params: AddCollectionEvent = {
            collectionName:
              dataCollection.dataCollection !== undefined
                ? dataCollection.dataCollection.name
                : '',
            messageTarget:
              dataCollection.messageSearchCatalog === true
                ? EventTarget.SEARCH_CATALOG_SELECTED_ITEMS_TAB
                : undefined,
          };
          const newEvent: Event = {
            eventTarget: EventTarget.SEARCH_CATALOG_SELECTED_ITEMS_TAB,
            eventType: EventType.DATA_COLLECTION_ALREADY_EXISTS,
            parameters: params,
          };
          dispatch(
            handleEvent({
              event: newEvent,
              ctx: {
                showErrorToast: (message: string) => showErrorToast(message, darkMode),
              },
            }),
          );
        }

        // HTTP 401 means the session has expired.
        else if (apiResult.status === 401) {
          console.log('createNewDataCollection 401.');
        }

        // HTTP 403 means the user is not a member of this project.
        else if (apiResult.status === 403) {
          // TODO: Display error message
          const params: AddCollectionEvent = {
            collectionName:
              dataCollection.dataCollection !== undefined
                ? dataCollection.dataCollection.name
                : '',
            messageTarget:
              dataCollection.messageSearchCatalog === true
                ? EventTarget.SEARCH_CATALOG_SELECTED_ITEMS_TAB
                : undefined,
          };
          const newEvent: Event = {
            eventTarget: EventTarget.SEARCH_CATALOG_SELECTED_ITEMS_TAB,
            eventType: EventType.USER_NOT_PROJECT_MEMBER_NEW_COLLECTION,
            parameters: params,
          };
          dispatch(
            handleEvent({
              event: newEvent,
              ctx: {
                showErrorToast: (message: string) => showErrorToast(message, darkMode),
              },
            }),
          );
        }

        // anything else, then display an error message.
        else {
          // TODO: Display error message
        }
      } catch (e) {}
    }
}

//	------------------------------------------------------------
//
//	Add to an existing collection in the database.
//
//	------------------------------------------------------------
async function _addToExistingDataCollection(
  event: Event,
  dispatch: AppDispatch,
  getState: () => RootState,
  signal?: AbortSignal,
) {
  const { eventTarget, eventType, parameters } = event;
  const dataCollection = parameters as DataCollection;
  const stateFetch = getState();
  const darkMode = stateFetch.global.darkMode;

  console.log('addToExistingDataCollection fires:');
  console.log(dataCollection);
  if (dataCollection.dataCollection !== undefined)
    if (dataCollection.dataCollection.id !== undefined) {
      var urlCommand: string =
        APIPrefix() +
        '/v1/add_to_data_collection' +
        '?data_collection_id=' +
        (dataCollection.dataCollection.id !== undefined
          ? dataCollection.dataCollection.id.toString()
          : '-1');

      try {
        const apiResult = await fetch(urlCommand, {
          method: 'PUT',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body:
            dataCollection.dids !== undefined
              ? JSON.stringify(dataCollection.dids)
              : JSON.stringify([]),
        });

        const returnedJson = await apiResult.json();

        // HTTP 201 means the items have been added to the collection.
        if (apiResult.status === 201) {
          // get the number of items added.
          var itemsAdded: number = 0;
          try {
            itemsAdded = returnedJson.additional_info.items_added;
          } catch (e) {}

          // Display confirmation message
          const params: AddCollectionEvent = {
            collectionName:
              dataCollection.dataCollection !== undefined
                ? dataCollection.dataCollection.name
                : '',
            itemsAdded: itemsAdded,
            messageTarget:
              dataCollection.messageSearchCatalog === true
                ? EventTarget.SEARCH_CATALOG_SELECTED_ITEMS_TAB
                : undefined,
          };
          const newEvent: Event = {
            eventTarget: eventTarget,
            eventType: EventType.ITEMS_ADDED_TO_DATA_COLLECTION,
            parameters: params,
          };
          dispatch(
            handleEvent({
              event: newEvent,
              ctx: {
                showSuccessToast: (message: string) =>
                  showSuccessToast(message, darkMode),
              },
            }),
          );
        }

        // HTTP 401 means the session has expired.
        else if (apiResult.status === 401) {
          console.log('addToExistingDataCollection 401.');
        }

        // HTTP 403 means the user is not a member of this project.
        else if (apiResult.status === 403) {
          const params: AddCollectionEvent = {
            projectName:
              dataCollection.project !== undefined ? dataCollection.project.name : '',
            messageTarget:
              dataCollection.messageSearchCatalog === true
                ? EventTarget.SEARCH_CATALOG_SELECTED_ITEMS_TAB
                : undefined,
          };
          const newEvent: Event = {
            eventTarget: eventTarget,
            eventType: EventType.USER_NOT_PROJECT_MEMBER_EXISTING_COLLECTION,
            parameters: params,
          };
          dispatch(
            handleEvent({
              event: newEvent,
              ctx: {
                showErrorToast: (message: string) => showErrorToast(message, darkMode),
              },
            }),
          );
        }

        // HTTP 404 means a data collection with this id does not exist.
        else if (apiResult.status === 404) {
          const params: AddCollectionEvent = {
            collectionName:
              dataCollection.dataCollection !== undefined
                ? dataCollection.dataCollection.name
                : '',
            messageTarget:
              dataCollection.messageSearchCatalog === true
                ? EventTarget.SEARCH_CATALOG_SELECTED_ITEMS_TAB
                : undefined,
          };
          const newEvent: Event = {
            eventTarget: eventTarget,
            eventType: EventType.DATA_COLLECTION_DOES_NOT_EXIST,
            parameters: params,
          };
          dispatch(
            handleEvent({
              event: newEvent,
              ctx: {
                showErrorToast: (message: string) => showErrorToast(message, darkMode),
              },
            }),
          );
        }
        // anything else, then display an error message.
        else {
          // TODO: Display error message
        }
      } catch (e) {}
    }
}

//	------------------------------------------------------------
//
//	Remove items from a data collection in the database.
//
//	------------------------------------------------------------
async function _removeFromDataCollection(
  event: Event,
  dispatch: AppDispatch,
  getState: () => RootState,
  signal?: AbortSignal,
) {
  const { eventTarget, eventType, parameters } = event;
  const dataCollection = parameters as DataCollection;
  const stateFetch = getState();
  const darkMode = stateFetch.global.darkMode;

  console.log('removeFromDataCollection fires:');
  console.log(dataCollection);
  if (dataCollection.dataCollection !== undefined)
    if (dataCollection.dataCollection.id !== undefined) {
      var urlCommand: string =
        APIPrefix() +
        '/v1/remove_from_data_collection' +
        '?data_collection_id=' +
        dataCollection.dataCollection.id.toString();

      try {
        const apiResult = await fetch(urlCommand, {
          method: 'PUT',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body:
            dataCollection.dids !== undefined
              ? JSON.stringify(dataCollection.dids)
              : JSON.stringify([]),
        });

        const returnedJson = await apiResult.json();

        // HTTP 200 means the items have been removed from the collection.
        if (apiResult.status === 200) {
          // get the number of items removed.
          var itemsRemoved: number = 0;
          try {
            itemsRemoved = returnedJson.additional_info.items_removed;
          } catch (e) {}

          // raise a deleted-items-from-collection event.
          const newEvent: Event = {
            eventTarget: eventTarget,
            eventType: EventType.ITEMS_REMOVED_FROM_DATA_COLLECTION,
          };
          dispatch(
            handleEvent({
              event: newEvent,
              ctx: {
                showSuccessToast: (message: string) =>
                  showSuccessToast(message, darkMode),
              },
            }),
          );
        }

        // HTTP 401 means the session has expired.
        else if (apiResult.status === 401) {
          console.log('removeFromDataCollection 401.');
        }

        // HTTP 403 means the user is not a member of this project.
        else if (apiResult.status === 403) {
          const newEvent: Event = {
            eventTarget: eventTarget,
            eventType: EventType.USER_NOT_PROJECT_MEMBER_EXISTING_COLLECTION,
          };
          dispatch(
            handleEvent({
              event: newEvent,
              ctx: {
                showSuccessToast: (message: string) =>
                  showSuccessToast(message, darkMode),
              },
            }),
          );
        }

        // HTTP 404 means a data collection with this id does not exist.
        else if (apiResult.status === 404) {
          const newEvent: Event = {
            eventTarget: eventTarget,
            eventType: EventType.DATA_COLLECTION_DOES_NOT_EXIST,
          };
          dispatch(
            handleEvent({
              event: newEvent,
              ctx: {
                showSuccessToast: (message: string) =>
                  showSuccessToast(message, darkMode),
              },
            }),
          );
        }

        // anything else, then display an error message.
        /*else {
            // TODO: Display error message
          }*/
      } catch (e) {}
    }
}

//	------------------------------------------------------------
//
//	Delete a data collection from the database.
//
//	------------------------------------------------------------
async function _deleteDataCollection(
  event: Event,
  dispatch: AppDispatch,
  getState: () => RootState,
  signal?: AbortSignal,
) {
  const { eventTarget, eventType, parameters } = event;
  const dataCollection = parameters as DataCollection;
  const stateFetch = getState();
  const darkMode = stateFetch.global.darkMode;

  console.log('deleteDataCollection fires:');
  console.log(dataCollection);
  // set the project name.
  const project: string | undefined =
    dataCollection.project !== undefined ? dataCollection.project.name : undefined;

  if (dataCollection.collectionIDs !== undefined)
    if (dataCollection.collectionIDs.length > 0) {
      var urlCommand: string = APIPrefix() + '/v1/delete_data_collections';

      try {
        const apiResult = await fetch(urlCommand, {
          method: 'PUT',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body:
            dataCollection.collectionIDs !== undefined
              ? JSON.stringify(dataCollection.collectionIDs)
              : JSON.stringify([]),
        });

        const returnedJson = await apiResult.json();

        // HTTP 200 means the data collection has been deleted.
        if (apiResult.status === 200) {
          // raise a deleted-collection event.
          const newEvent: Event = {
            eventTarget: eventTarget,
            eventType: EventType.DATA_COLLECTION_DELETED,
            parameters: project,
          };
          dispatch(
            handleEvent({
              event: newEvent,
              ctx: {
                showSuccessToast: (message: string) =>
                  showSuccessToast(message, darkMode),
              },
            }),
          );
          //
        }

        // HTTP 401 means the session has expired.
        else if (apiResult.status === 401) {
          console.log('deleteDataCollection 401.');
        }

        // HTTP 403 means the user is not a member of this project.
        else if (apiResult.status === 403) {
          const newEvent: Event = {
            eventTarget: eventTarget,
            eventType: EventType.USER_NOT_PROJECT_MEMBER_EXISTING_COLLECTION,
            parameters: project,
          };
          dispatch(
            handleEvent({
              event: newEvent,
              ctx: {
                showErrorToast: (message: string) => showErrorToast(message, darkMode),
              },
            }),
          );
        }

        // HTTP 404 means a data collection with this id does not exist.
        else if (apiResult.status === 404) {
          const newEvent: Event = {
            eventTarget: eventTarget,
            eventType: EventType.DATA_COLLECTION_DOES_NOT_EXIST,
            parameters: project,
          };
          dispatch(
            handleEvent({
              event: newEvent,
              ctx: {
                showErrorToast: (message: string) => showErrorToast(message, darkMode),
              },
            }),
          );
        }

        // anything else, then display an error message.
        /*else {
            // TODO: Display error message
          }*/
      } catch (e) {}
    }
}

//	-------------------------------------------------
//
//	load log entries from the backend API
//
//	-------------------------------------------------

async function _loadBackendLogs(
  event: MessageEvent,
  dispatch: Dispatch,
  signal?: AbortSignal,
) {
  // Adds an error message to the log table.
  const logError = (message: string) => {
    var logEntry: GatewayLogType = {
      user_id: null,
      log_id: '',
      level: 40,
      message: message,
      component: 'FRONTEND',
      created_at: new Date().toISOString(),
      metadata: {},
    };
    dispatch(prependGatewayLogs(logEntry));
    console.error(message); // Log to the browser console.
  };

  try {
    const apiResult = await fetch(APIPrefix() + '/v1/logs', {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    const rawJson = await apiResult.text(); // get raw text
    // Use JSON reviver/callback function to prefix backend log component strings.
    const parsedJson = JSON.parse(rawJson, (key, value) => {
      if (key === 'component' && typeof value === 'string') {
        return 'BACKEND: ' + value;
      }
      return value;
    });
    if (apiResult.status === 200) {
      // get logs list.
      if (Array.isArray(parsedJson.entries)) {
        const updatedLogs: GatewayLogType[] = [];

        parsedJson.entries.forEach((entry: any) => {
          const logEntry: GatewayLogType = {
            user_id: entry.user_id ?? null,
            log_id: String(entry.log_id ?? ''),
            level: Number(entry.level ?? 0),
            message: String(entry.message ?? ''),
            component: String(entry.component ?? ''),
            created_at: String(entry.created_at ?? new Date().toISOString()),
            metadata: entry.metadata ?? {},
          };
          updatedLogs.push(logEntry);
        });

        dispatch(setGatewayLogs(updatedLogs));
      }
    } else if (apiResult.status !== 404) {
      // Log an error to the table for unexpected status codes.
      var message = `/get_logs endpoint returned status code: ${apiResult.status}.`;
      if (parsedJson.detail !== undefined)
        message = `${message} Error details: ${parsedJson.detail}.`;
      logError(message);
    }
  } catch (e) {
    // Log an error to the table for caught exceptions.
    var message = `Error calling /logs endpoint: ${e}.`;
    logError(message);
  }
} // _loadBackendLogs

/**
 * Thunk wrapper to dispatch backend logs
 */
export const loadBackendLogs = createAppAsyncThunk(
  'app/loadBackendLogs',
  async (event: MessageEvent | undefined, thunkAPI) => {
    await _loadBackendLogs(
      event ?? new MessageEvent('manual'),
      thunkAPI.dispatch,
      thunkAPI.signal,
    );
  },
);

/**
 * Global Event Handling System for the Gateway.
 *
 * Receives a domain {@link Event} and performs the appropriate side effects by:
 * - Updating global/app state (e.g. menus, preferences, service status).
 * - Triggering async workflows (e.g. compute search, DM search, notifications, jobs).
 * - Delegating component-scoped events via eventHandlers (File Browser, Search Catalog, Home, etc).
 * - Optionally invoking UI-only effects (navigation, toasts) via the injected context.
 *
 * Notes:
 * - UI capabilities are provided through {@link NavigateEventContext}. When not provided, UI effects are skipped.
 * - Component-specific events are forwarded to eventHandler thunks such as {@link handleFileBrowserEvent} and
 *   {@link handleSearchCatalogEvent} based on {@link eventTarget}.
 *
 * @param args - Payload for the thunk.
 * @param args.event - The event to handle.
 * @param args.ctx - Optional UI context (e.g. {@link navigate}, toast helpers).
 * @param thunkAPI - Redux Toolkit thunk API (dispatch/getState/signal?).
 * @returns A promise that resolves when the event has been handled.
 *
 * @example
 * dispatch(handleEvent({ event, ctx: { navigate } }));
 *
 * @example
 * // Domain-only execution (no UI invoking)
 * dispatch(handleEvent({ event }));
 */
export const handleEvent = createAppAsyncThunk(
  'app/handleEvent',
  async (args: { event: Event; ctx?: NavigateEventContext }, thunkAPI) => {
    const stateFetch = thunkAPI.getState();
    const state = stateFetch.global;

    switch (args.event.eventType) {
      // Some cases will use if statement to determine the
      // eventTarget and dispatch to per-component eventHandler()
      case EventType.INITIATE_COMPUTE_SEARCH: {
        const params = args.event.parameters as InitiateComputeSearchParams;

        if (params?.showPage) {
          thunkAPI.dispatch(setSelectedTool(ToolType.SEARCH_COMPUTE));
          args.ctx?.navigate?.(toolTypeToPath[ToolType.SEARCH_COMPUTE]);
        }

        thunkAPI.dispatch(loadComputeData());
        break;
      }

      case EventType.INITIATE_DM_SEARCH: {
        // eventTargets: App, File Browser, User Control & Search Catalog
        const params = args.event.parameters as InitiateDMSearchParams;

        if (args.event.eventTarget === EventTarget.DATA_MANAGEMENT_TABLE) {
          console.log(`Page: ${JSON.stringify(params)}`);
          if (params?.showPage) {
            thunkAPI.dispatch(setSelectedTool(ToolType.FILE_BROWSER));
            args.ctx?.navigate?.(toolTypeToPath[ToolType.FILE_BROWSER]);
          }

          thunkAPI.dispatch(initiateDMSearch(params));
        }
        // if (args.event.eventTarget === EventTarget.DATA_MANAGEMENT_TABLE) {
        //   thunkAPI.dispatch(handleFileBrowserEvent({event: args.event}));
        // }
        else if (args.event.eventTarget === EventTarget.USER_CONTROL) {
          // TODO: I need to dispatch to User Control eventHandler
        } else if (args.event.eventTarget === EventTarget.SEARCH_CATALOG) {
          // TODO: I need to dispatch to Search Catalog eventHandler
          //thunkAPI.dispatch(handleFileBrowserEvent({event: args.event}));
        }

        break;
      }

      case EventType.INITIATE_DATA_SEARCH:
        thunkAPI.dispatch(setSelectedTool(ToolType.SEARCH_CATALOG));
        args.ctx?.navigate?.(toolTypeToPath[ToolType.SEARCH_CATALOG]);
        break;

      // initiate a project-files search.
      case EventType.INITIATE_PROJECT_FILES_SEARCH:
        thunkAPI.dispatch(getProjectFiles());
        break;

      // NOTES: Crucial function that get latest jobs.
      case EventType.GET_DATA_MANAGEMENT_JOBS:
        thunkAPI.dispatch(fetchJobs());
        break;

      case EventType.SET_PREFERENCES:
        thunkAPI.dispatch(setPreferences(args.event.parameters));
        break;

      case EventType.COMPUTE_SERVICE_STATUS_CHANGED:
        thunkAPI.dispatch(setUpdateServiceStatus(args.event.parameters));
        break;

      case EventType.LOAD_BACKEND_LOGS:
        thunkAPI.dispatch(loadBackendLogs(new MessageEvent('manual')));
        break;

      case EventType.GET_COMPUTE_JOBS:
      case EventType.COMPUTE_JOB_ADDED:
      case EventType.COMPUTE_JOB_UPDATED:
      case EventType.COMPUTE_JOB_DELETED:
        thunkAPI.dispatch(fnD_getComputeJobs());
        break;

      // UI-effect intents only
      // TODO: Need finish refactoring launchNotebook function
      // case EventType.LAUNCH_NOTEBOOK:
      //   await _launchNotebook(event.parameters));
      //   break;

      case EventType.GET_NOTIFICATIONS:
      case EventType.REFRESH_NOTIFICATIONS:
        thunkAPI.dispatch(getNotifications());
        break;

      case EventType.DELETE_NOTIFICATIONS:
        await _deleteNotifications(
          args.event.parameters,
          thunkAPI.dispatch,
          thunkAPI.getState,
        );
        break;

      case EventType.MARK_NOTIFICATIONS_READ:
        await _markNotificationsRead(
          args.event.parameters,
          thunkAPI.dispatch,
          thunkAPI.getState,
        );
        break;

      case EventType.MOVE_DATA_TO_STORAGE:
        console.log(
          `Event Type: ${EventType.MOVE_DATA_TO_STORAGE} - Data: ${JSON.stringify(args.event.parameters)}`,
        );
        await _moveDataToStorage(args.event.parameters, thunkAPI.dispatch);
        break;

      // Data Collection Events
      case EventType.CREATE_NEW_DATA_COLLECTION:
        await _createNewDataCollection(args.event, thunkAPI.dispatch, thunkAPI.getState);
        break;

      case EventType.ADD_TO_EXISTING_DATA_COLLECTION:
        await _addToExistingDataCollection(
          args.event,
          thunkAPI.dispatch,
          thunkAPI.getState,
        );
        break;

      case EventType.REMOVE_FROM_DATA_COLLECTION:
        await _removeFromDataCollection(args.event, thunkAPI.dispatch, thunkAPI.getState);
        break;

      case EventType.DELETE_DATA_COLLECTION:
        await _deleteDataCollection(args.event, thunkAPI.dispatch, thunkAPI.getState);
        break;

      case EventType.DATA_COLLECTION_ADDED:
        if (args.event.eventTarget === EventTarget.SEARCH_CATALOG) {
          thunkAPI.dispatch(
            handleSearchCatalogEvent({
              event: args.event,
              ctx: {
                showSuccessToast: (message: string) =>
                  showSuccessToast(message, state.darkMode),
              },
            }),
          );
        } else if (args.event.eventTarget === EventTarget.DATA_MANAGEMENT_TABLE) {
          thunkAPI.dispatch(
            handleFileBrowserEvent({
              event: args.event,
              ctx: {
                showSuccessToast: (message: string) =>
                  showSuccessToast(message, state.darkMode),
              },
            }),
          );
        }

        break;

      case EventType.DATA_COLLECTION_DELETED:
        if (args.event.eventTarget === EventTarget.SEARCH_CATALOG) {
          thunkAPI.dispatch(
            handleSearchCatalogEvent({
              event: args.event,
              ctx: {
                showSuccessToast: (message: string) =>
                  showSuccessToast(message, state.darkMode),
              },
            }),
          );
        } else if (args.event.eventTarget === EventTarget.DATA_MANAGEMENT_TABLE) {
          thunkAPI.dispatch(
            handleFileBrowserEvent({
              event: args.event,
              ctx: {
                showSuccessToast: (message: string) =>
                  showSuccessToast(message, state.darkMode),
              },
            }),
          );
        }

        break;

      case EventType.DATA_COLLECTION_DOES_NOT_EXIST:
        if (args.event.eventTarget === EventTarget.SEARCH_CATALOG) {
          thunkAPI.dispatch(
            handleSearchCatalogEvent({
              event: args.event,
              ctx: {
                showErrorToast: (message: string) =>
                  showErrorToast(message, state.darkMode),
              },
            }),
          );
        } else if (args.event.eventTarget === EventTarget.DATA_MANAGEMENT_TABLE) {
          thunkAPI.dispatch(
            handleFileBrowserEvent({
              event: args.event,
              ctx: {
                showErrorToast: (message: string) =>
                  showErrorToast(message, state.darkMode),
              },
            }),
          );
        }
        break;

      case EventType.DATA_COLLECTION_ALREADY_EXISTS:
        if (args.event.eventTarget === EventTarget.SEARCH_CATALOG) {
          thunkAPI.dispatch(
            handleSearchCatalogEvent({
              event: args.event,
              ctx: {
                showErrorToast: (message: string) =>
                  showErrorToast(message, state.darkMode),
              },
            }),
          );
        } else if (args.event.eventTarget === EventTarget.DATA_MANAGEMENT_TABLE) {
          thunkAPI.dispatch(
            handleFileBrowserEvent({
              event: args.event,
              ctx: {
                showErrorToast: (message: string) =>
                  showErrorToast(message, state.darkMode),
              },
            }),
          );
        }
        break;

      case EventType.ITEMS_ADDED_TO_DATA_COLLECTION:
        if (args.event.eventTarget === EventTarget.SEARCH_CATALOG) {
          thunkAPI.dispatch(
            handleSearchCatalogEvent({
              event: args.event,
              ctx: {
                showSuccessToast: (message: string) =>
                  showSuccessToast(message, state.darkMode),
              },
            }),
          );
        } else if (args.event.eventTarget === EventTarget.DATA_MANAGEMENT_TABLE) {
          thunkAPI.dispatch(
            handleFileBrowserEvent({
              event: args.event,
              ctx: {
                showSuccessToast: (message: string) =>
                  showSuccessToast(message, state.darkMode),
              },
            }),
          );
        }
        break;

      case EventType.ITEMS_REMOVED_FROM_DATA_COLLECTION:
        if (args.event.eventTarget === EventTarget.SEARCH_CATALOG) {
          thunkAPI.dispatch(
            handleSearchCatalogEvent({
              event: args.event,
              ctx: {
                showSuccessToast: (message: string) =>
                  showSuccessToast(message, state.darkMode),
              },
            }),
          );
        } else if (args.event.eventTarget === EventTarget.DATA_MANAGEMENT_TABLE) {
          thunkAPI.dispatch(
            handleFileBrowserEvent({
              event: args.event,
              ctx: {
                showSuccessToast: (message: string) =>
                  showSuccessToast(message, state.darkMode),
              },
            }),
          );
        }
        break;

      case EventType.USER_NOT_PROJECT_MEMBER_EXISTING_COLLECTION:
        if (args.event.eventTarget === EventTarget.SEARCH_CATALOG) {
          thunkAPI.dispatch(
            handleSearchCatalogEvent({
              event: args.event,
              ctx: {
                showErrorToast: (message: string) =>
                  showErrorToast(message, state.darkMode),
              },
            }),
          );
        } else if (args.event.eventTarget === EventTarget.DATA_MANAGEMENT_TABLE) {
          thunkAPI.dispatch(
            handleFileBrowserEvent({
              event: args.event,
              ctx: {
                showErrorToast: (message: string) =>
                  showErrorToast(message, state.darkMode),
              },
            }),
          );
        }
        break;

      // Data Query Events
      case EventType.EXECUTE_ADQL_QUERY:
        thunkAPI.dispatch(
          handleSearchCatalogEvent({
            event: args.event,
          }),
        );
        break;

      case EventType.DATA_QUERY_ADDED:
      case EventType.DATA_QUERY_DELETED:
        thunkAPI.dispatch(
          handleSearchCatalogEvent({
            event: args.event,
          }),
        );
        break;

      // Aladin Events
      case EventType.UPDATE_SEARCH_RA_DEC:
        thunkAPI.dispatch(
          handleSearchCatalogEvent({
            event: args.event,
          }),
        );
        break;

      // Project Events
      case EventType.API_STATUS_UPDATE:
        thunkAPI.dispatch(
          handleHomeEvent({
            event: args.event,
          }),
        );
        break;
    }
  },
);

// Intializer
export const initializeAppData = (): AppThunk => async (dispatch, getState) => {
  // Guard: run only when logged in (or allow caller to check)
  const stateFetch: any = getState();
  const state: GlobalState = stateFetch.global;

  if (!state.loggedIn) {
    return;
  }

  const controller = new AbortController();
  const signal = controller.signal;

  try {
    // Parallelizable independent loads:
    // user settings, storage areas, data management jobs, backend token can run in parallel
    const parallelTasks = [
      //TODO: Update these in PI29
      //_loadUserSettings(dispatch, getState, signal),
      //_loadStorageAreas(dispatch, getState, signal),
      //_getDataManagementJobs(dispatch, getState, signal),
      _retrieveBackendToken(dispatch, getState, signal),
    ];

    // Start parallel tasks
    const parallelPromise = Promise.allSettled(parallelTasks);

    // Start loading projects in parallel as well
    const loadProjectsPromise = _loadProjects(dispatch, getState, signal);

    try {
      await _loadSites(dispatch, getState, signal);
      await loadStorageAreas();
      await _loadComputeServices(dispatch, getState, signal);
      await _getCanfarServices(dispatch, getState, signal);
    } catch (seqErr) {
      console.error(
        'Error during sequential loading (sites -> compute -> canfar):',
        seqErr,
      );
    }

    try {
      await loadProjectsPromise;
    } catch (projErr) {
      console.error('Error loading projects:', projErr);
    }

    // Wait for everything
    await parallelPromise;
  } catch (err) {
    console.error('initializeAppData failed', err);
  } finally {
    // NOTES: Need to discuss the below comment during PI29. We need to start handling errors in the frontend
    // If you want to allow external cancellation, return controller or have the hook keep it
  }
};
